/**
 * Print Service - Multi-method thermal printing
 *
 * Priority: 1) QZ Tray (RAW ESC/POS) → 2) Web Serial → 3) WebUSB → 4) Browser print
 *
 * QZ Tray is the PREFERRED method when connected because:
 * - Supports any printer that has a Windows driver installed
 * - Prints RAW ESC/POS commands silently (no Chrome dialog)
 * - Works with USB, network, and Bluetooth printers
 *
 * IMPORTANT: The printer MUST be selected by the user first.
 * "Generic / Text Only" will NOT work - must be the actual thermal printer.
 */

import { isUSBPrinterConnected, printViaUSB, reconnectUSBPrinter } from "./webUSBPrintService";
import { isSerialPrinterConnected, printViaSerial, reconnectSerialPrinter } from "./webSerialPrintService";
import { printReceiptSilent } from "./silentPrintService";
import { buildProfessionalReceiptString, buildFiscalReceiptString } from "./receiptBuilder";
import { getCurrentLanguage } from "./receiptTranslations";
import { getItemComponents } from "./receiptComponents";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;
let qzModule = null;
let qzConnected = false;
let qzSecurityConfigured = false;
let qzBlocked = false;

// Saved printer name (persisted in localStorage)
const PRINTER_KEY = "kaiso_selected_printer";
const PRINT_METHOD_KEY = "kaiso_print_method";

export function getSavedPrinter() {
  return localStorage.getItem(PRINTER_KEY) || "";
}

export function savePrinter(name) {
  localStorage.setItem(PRINTER_KEY, name);
}

export function getSavedMethod() {
  return localStorage.getItem(PRINT_METHOD_KEY) || "auto";
}

export function saveMethod(method) {
  localStorage.setItem(PRINT_METHOD_KEY, method);
}

async function getQZ() {
  if (!qzModule) {
    try {
      qzModule = (await import("qz-tray")).default;
    } catch {
      console.warn("QZ Tray module not available");
      return null;
    }
  }
  return qzModule;
}

async function configureQZSecurity(qz) {
  if (qzSecurityConfigured) return;

  // QZ Tray default is SHA512 since version 2.1+
  let signAlgorithm = "SHA512";
  if (qz.security.setSignatureAlgorithm) {
    qz.security.setSignatureAlgorithm("SHA512");
  }

  qz.security.setCertificatePromise(function (resolve, reject) {
    console.log("[QZ] Fetching certificate from:", `${API}/api/qz/cert`);
    fetch(`${API}/api/qz/cert`, {cache: 'no-store'})
      .then(r => {
        if (!r.ok) throw new Error(`Cert fetch HTTP ${r.status}`);
        return r.text();
      })
      .then(cert => {
        console.log("[QZ] Certificate loaded OK, length:", cert.length);
        resolve(cert);
      })
      .catch(err => {
        console.error("[QZ] Certificate fetch FAILED:", err);
        reject(err);
      });
  });

  qz.security.setSignaturePromise(function (toSign) {
    return function (resolve, reject) {
      console.log("[QZ] Signing request with", signAlgorithm, "- data length:", toSign?.length);
      fetch(`${API}/api/qz/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request: toSign, algorithm: signAlgorithm }),
        cache: 'no-store',
      })
        .then(r => {
          if (!r.ok) throw new Error(`Sign HTTP ${r.status}`);
          return r.text();
        })
        .then(sig => {
          console.log("[QZ] Signature OK, length:", sig.length);
          resolve(sig);
        })
        .catch(err => {
          console.error("[QZ] Signature FAILED:", err);
          reject(err);
        });
    };
  });

  qzSecurityConfigured = true;
}

/**
 * Connect to QZ Tray.
 * @param {object} opts - { manual: false }
 */
export async function connectQZTray(opts = {}) {
  const { manual = false } = opts;
  if (qzBlocked && !manual) return false;

  const qz = await getQZ();
  if (!qz) {
    console.error("[QZ] Module not loaded - qz-tray package may be missing");
    return false;
  }

  try {
    await configureQZSecurity(qz);

    if (qz.websocket.isActive()) {
      console.log("[QZ] Already connected (websocket active)");
      qzConnected = true;
      return true;
    }

    if (qzConnected) {
      try { await qz.websocket.disconnect(); } catch {}
      qzConnected = false;
    }

    console.log("[QZ] Connecting to QZ Tray via WebSocket...");
    // Try to connect with retries for manual mode
    const retries = manual ? 2 : 0;
    await qz.websocket.connect({ retries, delay: 1 });
    console.log("[QZ] Connected successfully!");
    qzConnected = true;
    qzBlocked = false;
    return true;
  } catch (err) {
    const msg = err?.message || String(err);
    console.error("[QZ] Connection failed:", msg);
    
    // If SHA512 failed, try resetting security config to try SHA256
    if (msg.includes("certificate") || msg.includes("signature") || msg.includes("CLOSE_REASON")) {
      console.log("[QZ] Trying with reset security config...");
      qzSecurityConfigured = false;
      try {
        // Force SHA256 on second attempt
        if (qz.security.setSignatureAlgorithm) {
          qz.security.setSignatureAlgorithm("SHA256");
        }
        qz.security.setSignaturePromise(function (toSign) {
          return function (resolve, reject) {
            fetch(`${API}/api/qz/sign`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ request: toSign, algorithm: "SHA256" }),
            })
              .then(r => r.ok ? r.text() : Promise.reject("Sign SHA256 failed"))
              .then(resolve)
              .catch(reject);
          };
        });
        qzSecurityConfigured = true;
        await qz.websocket.connect({ retries: 0, delay: 0 });
        console.log("[QZ] Connected with SHA256 fallback!");
        qzConnected = true;
        qzBlocked = false;
        return true;
      } catch (err2) {
        console.error("[QZ] SHA256 fallback also failed:", err2?.message);
      }
    }
    
    qzConnected = false;
    if (!manual) qzBlocked = true;
    // Provide user-friendly error context
    if (msg.includes("Unable to establish")) {
      throw new Error("QZ Tray nao esta rodando. Abra o QZ Tray e tente novamente. Se acabou de instalar, reinicie o computador.");
    }
    if (msg.includes("CLOSE_REASON_NORMAL") || msg.includes("1000")) {
      throw new Error("QZ Tray rejeitou a conexao. Execute o instalador v8 novamente como Administrador e reinicie o QZ Tray.");
    }
    if (msg.includes("certificate") || msg.includes("Certificate")) {
      throw new Error("Certificado rejeitado pelo QZ Tray. Execute o instalador v8 como Administrador e reinicie o QZ Tray.");
    }
    throw new Error(msg);
  }
}

export function isQZConnected() {
  return qzConnected;
}

export function resetQZBlock() {
  qzBlocked = false;
  qzSecurityConfigured = false;
}

/**
 * Run diagnostic checks for printer connectivity
 * Returns detailed status of all print methods and QZ security
 */
export async function runPrinterDiagnostic() {
  const results = {
    timestamp: new Date().toISOString(),
    apiUrl: API,
    qzModule: false,
    qzWebsocketActive: false,
    certEndpoint: { ok: false, error: null },
    signEndpoint: { ok: false, error: null },
    backendDiagnostic: null,
    webSerial: typeof navigator !== 'undefined' && 'serial' in navigator,
    webUSB: typeof navigator !== 'undefined' && !!navigator.usb,
  };

  // Check QZ module
  try {
    const qz = await getQZ();
    results.qzModule = !!qz;
    if (qz) {
      results.qzVersion = qz.version || "unknown";
      results.qzWebsocketActive = qz.websocket.isActive();
    }
  } catch (e) {
    results.qzModuleError = e.message;
  }

  // Test cert endpoint
  try {
    const r = await fetch(`${API}/api/qz/cert`);
    const text = await r.text();
    results.certEndpoint = {
      ok: r.ok && text.includes("BEGIN CERTIFICATE"),
      status: r.status,
      length: text.length,
    };
  } catch (e) {
    results.certEndpoint = { ok: false, error: e.message };
  }

  // Test sign endpoint
  try {
    const r = await fetch(`${API}/api/qz/sign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ request: "diagnostic-test" }),
    });
    const text = await r.text();
    results.signEndpoint = {
      ok: r.ok && text.length > 10,
      status: r.status,
      signatureLength: text.length,
    };
  } catch (e) {
    results.signEndpoint = { ok: false, error: e.message };
  }

  // Backend diagnostic
  try {
    const r = await fetch(`${API}/api/qz/diagnostic`);
    results.backendDiagnostic = await r.json();
  } catch (e) {
    results.backendDiagnostic = { error: e.message };
  }

  console.log("[QZ DIAGNOSTIC]", JSON.stringify(results, null, 2));
  return results;
}

export async function getAvailablePrinters() {
  const qz = await getQZ();
  if (!qz || !qzConnected) return [];
  try {
    return await qz.printers.find();
  } catch { return []; }
}

/**
 * Build ESC/POS raw byte commands for thermal printer
 * Now uses the professional receipt builder with QR code and branding
 */
function buildESCPOS(order, options = {}) {
  // Build restaurant config from options
  const restaurant = {
    name: options.storeName || "Restaurante",
    address: options.address || "",
    phone: options.phone || "",
    cif: options.showCif ? options.cif : "",
    currency: options.currency || "eur",
    menu_url: options.menuUrl || "",
    slug: options.slug || "",
    show_qr_code: options.showQRCode !== false,
    qr_message: options.qrMessage || "Escaneie e veja nosso menu",
    qr_size: options.qrSize || 6,
    receipt_footer: options.receiptFooter || "Obrigado pela preferencia!",
    instagram_url: options.instagram || "",
    whatsapp_phone: options.whatsappPhone || "",
  };
  return buildProfessionalReceiptString(order, restaurant, { columns: options.columns || 42, language: options.language || getCurrentLanguage() });
}

/**
 * Print via QZ Tray (RAW ESC/POS).
 * This sends raw bytes directly to the thermal printer - NO Chrome dialog.
 * Auto-reconnects silently if QZ Tray dropped connection.
 */
async function printViaQZTray(order, options = {}) {
  const qz = await getQZ();
  if (!qz) throw new Error("QZ Tray not connected");

  // Auto-reconnect silently if websocket dropped
  if (!qz.websocket.isActive()) {
    try {
      await configureQZSecurity(qz);
      await qz.websocket.connect({ retries: 1, delay: 1 });
      qzConnected = true;
      qzBlocked = false;
    } catch {
      throw new Error("QZ Tray not connected");
    }
  }

  const printerName = options.printerName || getSavedPrinter();
  if (!printerName) {
    throw new Error("No printer selected. Please select a printer in Printer Setup.");
  }

  const escposData = buildESCPOS(order, options);
  const copies = options.copies || 1;

  // RAW type = sends directly to printer, no driver processing, no dialog
  const config = qz.configs.create(printerName, {
    copies: copies,
    // CRITICAL: Use 'raw' type for ESC/POS thermal printers
    // This bypasses the Windows print spooler and sends directly to the printer
  });

  // Send as raw data array
  const data = [escposData];
  await qz.print(config, data);

  return { success: true, method: "qz_tray", printer: printerName };
}

/**
 * Format receipt text for Serial/USB (plain text, no ESC/POS prefix)
 */
function formatReceiptText(order, options = {}) {
  const cols = options.columns || 35;
  const lines = [];

  if (options.showStoreName && options.storeName) {
    const pad = Math.max(0, Math.floor((cols - options.storeName.length) / 2));
    lines.push(" ".repeat(pad) + options.storeName.toUpperCase());
  }
  lines.push("=".repeat(cols));
  if (order.id) lines.push("Pedido: #" + (order.id || "").slice(-6).toUpperCase());
  if (order.customer_name) lines.push("Cliente: " + order.customer_name);
  if (order.table_number) lines.push("Mesa: " + order.table_number);
  const date = order.created_at ? new Date(order.created_at).toLocaleString("pt-BR") : new Date().toLocaleString("pt-BR");
  lines.push("Data: " + date);
  lines.push("=".repeat(cols));
  lines.push("ITENS");
  lines.push("-".repeat(cols));
  for (const item of (order.items || [])) {
    const qty = item.quantity || 1;
    const total = (qty * (item.unit_price || 0)).toFixed(2);
    lines.push(qty + "x " + (item.product_name || "Item"));
    for (const comp of getItemComponents(item)) {
      lines.push("  + " + comp.label + (comp.price ? " +" + comp.price.toFixed(2) : ""));
    }
    lines.push("  " + total + " EUR");
  }
  lines.push("-".repeat(cols));
  lines.push("TOTAL: " + (order.total || 0).toFixed(2) + " EUR");
  if (order.payment_method) lines.push("Pagamento: " + order.payment_method.toUpperCase());
  lines.push("=".repeat(cols));
  lines.push(options.receiptFooter || "Obrigado!");
  lines.push("");
  lines.push("");
  return lines.join("\n");
}

/**
 * Print an order receipt.
 *
 * If a specific method is saved (qz, serial, usb, browser), uses that method.
 * If "auto", tries in priority order: QZ Tray → Serial → USB → Browser.
 *
 * IMPORTANT: When QZ Tray is selected and connected, it prints RAW ESC/POS.
 * NO Chrome print dialog. Completely silent.
 */
export async function printReceipt(order, printerConfig = {}, restaurant = {}) {
  const method = getSavedMethod();
  const copies = printerConfig?.copies || 1;
  // Get language from restaurant.language, restaurant.locale (extract first part), or fallback to getCurrentLanguage()
  const language = restaurant?.language || 
    (restaurant?.locale ? restaurant.locale.split('-')[0] : null) || 
    getCurrentLanguage();

  const receiptOptions = {
    columns: printerConfig?.columns || 42,
    language,
    showStoreName: printerConfig?.show_store_name !== false,
    storeName: restaurant?.name || "Kaiso System",
    showCif: printerConfig?.show_cif || false,
    cif: restaurant?.cif || "",
    showOrderNumber: printerConfig?.show_order_number !== false,
    showOrderChannel: printerConfig?.show_order_channel !== false,
    receiptFooter: restaurant?.receipt_footer || restaurant?.receipt_config?.receipt_footer || "",
    printerName: printerConfig?.printer_name || getSavedPrinter(),
    copies,
    // Professional receipt fields
    address: restaurant?.address || restaurant?.receipt_config?.address || "",
    phone: restaurant?.phone || restaurant?.receipt_config?.phone || "",
    logoUrl: restaurant?.receipt_config?.logo_url || restaurant?.logo_url || "",
    currency: restaurant?.currency || "eur",
    menuUrl: restaurant?.receipt_config?.menu_url || restaurant?.menu_url || "",
    slug: restaurant?.slug || "",
    showQRCode: restaurant?.receipt_config?.show_qr_code !== false,
    qrMessage: restaurant?.receipt_config?.qr_message || "",
    qrSize: restaurant?.receipt_config?.qr_size || 6,
    instagram: restaurant?.instagram_url || restaurant?.receipt_config?.instagram || "",
    whatsappPhone: restaurant?.receipt_config?.whatsapp_phone || restaurant?.whatsapp_phone || "",
  };

  // If user selected a specific method, use ONLY that method (don't fallthrough)
  if (method === "qz") {
    try {
      return await printViaQZTray(order, receiptOptions);
    } catch (err) {
      return { success: false, method: "qz_tray", error: err.message };
    }
  }

  if (method === "serial") {
    try {
      if (isSerialPrinterConnected()) {
        return await printViaSerial(order, receiptOptions);
      }
      return { success: false, method: "serial", error: "Serial printer not connected" };
    } catch (err) {
      return { success: false, method: "serial", error: err.message };
    }
  }

  if (method === "usb") {
    try {
      if (isUSBPrinterConnected()) {
        return await printViaUSB(order, receiptOptions);
      }
      return { success: false, method: "usb", error: "USB printer not connected" };
    } catch (err) {
      return { success: false, method: "usb", error: err.message };
    }
  }

  if (method === "browser") {
    return await printReceiptSilent(order, receiptOptions);
  }

  // AUTO mode: try all methods in priority order
  // 1) QZ Tray (best for silent thermal printing)
  if (qzConnected && !qzBlocked && getSavedPrinter()) {
    try {
      return await printViaQZTray(order, receiptOptions);
    } catch (err) {
      console.warn("QZ auto-print failed:", err.message);
    }
  }

  // 2) Web Serial
  try {
    if (isSerialPrinterConnected()) {
      return await printViaSerial(order, receiptOptions);
    }
  } catch (err) {
    console.warn("Serial auto-print failed:", err.message);
  }

  // 3) WebUSB
  try {
    if (isUSBPrinterConnected()) {
      return await printViaUSB(order, receiptOptions);
    }
  } catch (err) {
    console.warn("USB auto-print failed:", err.message);
  }

  // 4) Browser print (last resort)
  return await printReceiptSilent(order, receiptOptions);
}

/**
 * Print a Vendus fiscal invoice (FATURA) silently on the thermal printer.
 * Falls back to browser window.print() if QZ/Serial/USB not available.
 *
 * @param {object} invoiceData  - From Vendus API response + order fields
 * @param {object} restaurant   - Restaurant data
 * @param {object} printerConfig - Optional overrides
 */
export async function printFiscalReceipt(invoiceData, restaurant = {}, printerConfig = {}) {
  const cols = printerConfig?.columns || 42;
  const language = restaurant?.language ||
    (restaurant?.locale ? restaurant.locale.split("-")[0] : null) ||
    getCurrentLanguage();

  const escpos = buildFiscalReceiptString(invoiceData, {
    name: restaurant?.name || "",
    address: restaurant?.address || "",
    phone: restaurant?.phone || "",
    fiscal_id: restaurant?.fiscal_id || restaurant?.cif || "",
    currency: restaurant?.currency || "eur",
    receipt_footer: restaurant?.receipt_footer || restaurant?.receipt_config?.receipt_footer || "",
    menu_url: restaurant?.receipt_config?.menu_url || restaurant?.menu_url || "",
    custom_domain: restaurant?.custom_domain || "",
    slug: restaurant?.slug || "",
    show_qr_code: restaurant?.receipt_config?.show_qr_code !== false,
    qr_message: restaurant?.receipt_config?.qr_message || restaurant?.qr_message || "",
    qr_size: restaurant?.receipt_config?.qr_size || restaurant?.qr_size || 5,
  }, { columns: cols, language });

  const printerName = printerConfig?.printer_name || getSavedPrinter();
  const method = getSavedMethod();

  // 1) QZ Tray (silent, no dialog)
  if ((method === "qz" || method === "auto") && printerName) {
    const qz = await getQZ();
    if (qz) {
      try {
        if (!qz.websocket.isActive()) {
          await configureQZSecurity(qz);
          await qz.websocket.connect({ retries: 1, delay: 1 });
        }
        const config = qz.configs.create(printerName);
        await qz.print(config, [{ type: "raw", format: "plain", data: escpos }]);
        return { success: true, method: "qz_tray" };
      } catch (err) {
        console.warn("[FiscalPrint] QZ failed, trying fallbacks:", err.message);
      }
    }
  }

  // 2) Web Serial
  if (method === "serial" || method === "auto") {
    try {
      if (isSerialPrinterConnected()) {
        await printViaSerial({ _rawString: escpos }, {});
        return { success: true, method: "serial" };
      }
    } catch {}
  }

  // 3) WebUSB
  if (method === "usb" || method === "auto") {
    try {
      if (isUSBPrinterConnected()) {
        await printViaUSB({ _rawString: escpos }, {});
        return { success: true, method: "usb" };
      }
    } catch {}
  }

  // 4) Browser fallback
  return await printReceiptSilent({ _rawString: escpos }, {});
}

/**
 * Auto-print on new order
 */
export async function autoPrintOrder(order, printStations = [], restaurant = {}) {
  // If using Kaiso Print desktop app, queue via API instead of browser printing
  const savedMethod = getSavedMethod();
  if (savedMethod === "desktop") {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) return;
      const { API_URL } = await import("./apiConfig");
      const apiBase = API_URL || "";
      await fetch(`${apiBase}/api/print/queue`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: order.id, station: "default", copies: 1 }),
      });
      console.log(`[KaisoPrint] Order ${order.id} queued for desktop printing`);
    } catch (err) {
      console.error("[KaisoPrint] Failed to queue print job:", err);
    }
    return;
  }

  const autoPrintStations = printStations.filter(s => s.auto_print);
  if (autoPrintStations.length === 0) {
    // No auto-print stations configured - DO NOT print automatically
    console.log(`[AutoPrint] Skipped - no stations have auto_print enabled for order ${order.id}`);
    return;
  }

  for (const station of autoPrintStations) {
    try {
      console.log(`[AutoPrint] Printing order ${order.id} to station ${station.alias || station.printer_name}`);
      await printReceipt(order, station, restaurant);
    } catch (err) {
      console.error(`[AutoPrint] Failed for ${station.alias || station.printer_name}:`, err);
    }
  }
}

/**
 * Build ESC/POS raw string for Fecho de Caixa report (80mm thermal)
 */
function buildCaixaESCPOS(reportData) {
  const { restaurantName, date, rows, totalRegistado, totalRecebido, totalDiff, valorInicial, totalVendas, cancelamentos } = reportData;
  const cols = 42;
  const ESC = "\x1B";
  const GS = "\x1D";
  const LF = "\n";
  const BOLD_ON = ESC + "E\x01";
  const BOLD_OFF = ESC + "E\x00";
  const CENTER = ESC + "a\x01";
  const LEFT = ESC + "a\x00";
  const CUT = GS + "V\x41\x03";
  const lr = (l, r, w = cols) => { const spaces = Math.max(1, w - l.length - r.length); return l + " ".repeat(spaces) + r; };
  const line = (c = "-") => c.repeat(cols);
  const ctr = (t) => { const pad = Math.max(0, Math.floor((cols - t.length) / 2)); return " ".repeat(pad) + t; };
  const fmt = (v) => v !== null && v !== undefined ? `E${Number(v).toFixed(2)}`.replace("E", "€") : "—";
  const fmtDiff = (d) => d === null ? "—" : d === 0 ? "€0,00" : d > 0 ? `+€${d.toFixed(2)}` : `-€${Math.abs(d).toFixed(2)}`;
  let s = "";
  s += CENTER;
  s += BOLD_ON + ctr("FECHO DE CAIXA") + LF + BOLD_OFF;
  if (restaurantName) s += ctr(restaurantName) + LF;
  s += ctr(date || new Date().toLocaleString("pt-PT")) + LF;
  s += LEFT;
  s += line("=") + LF;
  s += BOLD_ON + "RESUMO" + BOLD_OFF + LF;
  s += line("-") + LF;
  s += lr("Abertura:", fmt(valorInicial)) + LF;
  s += lr("Total vendas:", fmt(totalVendas)) + LF;
  if (cancelamentos > 0) s += lr("Cancelamentos:", fmt(cancelamentos)) + LF;
  s += line("=") + LF;
  s += BOLD_ON + "CONFERENCIA POR METODO" + BOLD_OFF + LF;
  s += line("-") + LF;
  const hdr = lr("Metodo", "Regist  Contado    Dif");
  s += hdr.substring(0, cols) + LF;
  s += line("-") + LF;
  for (const row of (rows || [])) {
    const name = (row.key || "").substring(0, 14);
    const reg = fmt(row.registado).padStart(7);
    const rec = (row.recVal !== null ? fmt(row.recVal) : "—").padStart(8);
    const dif = fmtDiff(row.diff).padStart(8);
    s += (name.padEnd(14) + " " + reg + " " + rec + " " + dif).substring(0, cols) + LF;
  }
  s += line("=") + LF;
  s += BOLD_ON;
  s += lr("TOTAL REGIST:", fmt(totalRegistado)) + LF;
  s += lr("TOTAL CONTADO:", totalRecebido !== null ? fmt(totalRecebido) : "—") + LF;
  s += lr("DIFERENCA:", fmtDiff(totalDiff)) + LF;
  s += BOLD_OFF;
  s += line("=") + LF;
  if (totalDiff === 0) s += CENTER + ctr("** CAIXA CONFERE **") + LF;
  else if (totalDiff !== null) s += CENTER + ctr(totalDiff > 0 ? `SOBRA ${fmt(totalDiff)}` : `FALTA ${fmt(Math.abs(totalDiff))}`) + LF;
  s += LEFT + LF + LF + LF;
  s += CUT;
  return s;
}

/**
 * Print Fecho de Caixa report on thermal printer.
 * Tries QZ Tray (silent, no dialog) first; falls back to browser window.print().
 */
export async function printCaixaReportThermal(reportData) {
  const method = localStorage.getItem("kaiso_print_method");
  const printerName = localStorage.getItem("kaiso_selected_printer");
  if ((method === "qz" || method === "auto") && printerName) {
    try {
      const qz = await getQZ();
      if (qz) {
        if (!qz.websocket.isActive()) {
          await configureQZSecurity(qz);
          await qz.websocket.connect({ retries: 1, delay: 1 });
        }
        const escData = buildCaixaESCPOS(reportData);
        const config = qz.configs.create(printerName);
        await qz.print(config, [{ type: "raw", format: "plain", data: escData }]);
        return { success: true, method: "qz_tray" };
      }
    } catch (err) {
      console.warn("[CaixaPrint] QZ Tray failed, falling back to browser print:", err.message);
    }
  }
  const { restaurantName, date, rows, totalRegistado, totalRecebido, totalDiff, valorInicial, totalVendas, cancelamentos } = reportData;
  const fmt = (v) => v !== null && v !== undefined ? `€${Number(v).toFixed(2)}` : "—";
  const fmtDiff = (d) => d === null ? "—" : d === 0 ? "€0,00" : d > 0 ? `+€${d.toFixed(2)}` : `-€${Math.abs(d).toFixed(2)}`;
  const rowsHtml = (rows || []).map(row =>
    `<tr><td>${row.key || ""}</td><td style="text-align:right">${fmt(row.registado)}</td><td style="text-align:right">${row.recVal !== null ? fmt(row.recVal) : "—"}</td><td style="text-align:right;font-weight:bold;color:${row.diff === 0 ? "#16a34a" : row.diff !== null && Math.abs(row.diff) < 5 ? "#d97706" : "#dc2626"}">${fmtDiff(row.diff)}</td></tr>`
  ).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>@page{size:80mm auto;margin:2mm}body{font-family:'Courier New',monospace;font-size:10px;width:72mm;margin:0;padding:4px}h2{text-align:center;font-size:12px;margin:3px 0}p{text-align:center;margin:1px 0;font-size:9px}table{width:100%;border-collapse:collapse;font-size:9px;margin:4px 0}th{border-bottom:1px solid #000;padding:2px;font-size:8px;text-align:left}td{padding:2px}.sep{border-top:1px dashed #000;margin:3px 0}.totrow{border-top:1px solid #000;font-weight:bold}.result{text-align:center;border:1px solid #000;padding:4px;font-weight:bold;font-size:11px;margin:4px 0}</style></head><body><h2>FECHO DE CAIXA</h2>${restaurantName ? `<p>${restaurantName}</p>` : ""}<p>${date || new Date().toLocaleString("pt-PT")}</p><div class="sep"></div><table><tr><td>Abertura:</td><td style="text-align:right">${fmt(valorInicial)}</td></tr><tr><td><b>Total vendas:</b></td><td style="text-align:right"><b>${fmt(totalVendas)}</b></td></tr>${cancelamentos > 0 ? `<tr><td>Cancelamentos:</td><td style="text-align:right">${fmt(cancelamentos)}</td></tr>` : ""}</table><div class="sep"></div><table><thead><tr><th>Método</th><th style="text-align:right">Regist.</th><th style="text-align:right">Contado</th><th style="text-align:right">Dif.</th></tr></thead><tbody>${rowsHtml}</tbody><tr class="totrow"><td>Total</td><td style="text-align:right">${fmt(totalRegistado)}</td><td style="text-align:right">${totalRecebido !== null ? fmt(totalRecebido) : "—"}</td><td style="text-align:right">${fmtDiff(totalDiff)}</td></tr></table><div class="result">${totalDiff === null ? "Caixa fechado" : totalDiff === 0 ? "✓ CAIXA CONFERE" : totalDiff > 0 ? `SOBRA ${fmt(totalDiff)}` : `FALTA ${fmt(Math.abs(totalDiff))}`}</div></body></html>`;
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0";
  document.body.appendChild(iframe);
  iframe.src = url;
  iframe.onload = () => {
    try { iframe.contentWindow.print(); } catch { }
    setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 8000);
  };
  return { success: true, method: "browser" };
}
